import { BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGame } from "@/context/GameContext";
import { LAYERS } from "@/lib/layers";
import { SCIENTIFIC_BASIS } from "@/lib/scientificBasis";
import { t } from "@/lib/translations";

function GameScienceBlock({ gKey, layer }) {
  const { language } = useGame();
  const sb = SCIENTIFIC_BASIS[gKey];
  if (!sb) return null;
  return (
    <div
      className="p-3 rounded-2xl bg-slate-50 border-l-4"
      style={{ borderColor: layer.color }}
      data-testid={`science-game-${gKey}`}
    >
      <div className="font-display font-bold text-base" style={{ color: layer.colorDark }}>
        {t(`g_${gKey}`, language)}
      </div>
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-1">
        {sb.domain[language]}
      </div>
      <p className="text-sm text-slate-700 mt-2 leading-relaxed">{sb.rationale[language]}</p>
      <div className="mt-2 space-y-1">
        {sb.references.map((ref) => (
          <div key={`${ref.authors}-${ref.year}`} className="text-xs text-slate-600">
            <span className="font-bold">
              {ref.authors} ({ref.year}).
            </span>{" "}
            <em>{ref.title}</em>. {ref.journal}.{" "}
            <span
              className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white align-middle"
              style={{ background: layer.color }}
            >
              {ref.index}
            </span>
            {ref.url && (
              <>
                {" "}
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold hover:underline"
                  style={{ color: layer.colorDark }}
                >
                  [{language === "id" ? "buka" : "link"}]
                </a>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScienceHeader() {
  const { language } = useGame();
  return (
    <Card className="rounded-3xl bg-white border-4 border-sky-200">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-sky-100 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-6 h-6 text-sky-600" strokeWidth={3} />
          </div>
          <div>
            <h3 className="font-display font-bold text-xl text-slate-800">
              {language === "id"
                ? "Dasar Ilmiah Setiap Permainan"
                : "Scientific Basis of Each Game"}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {language === "id"
                ? "Setiap permainan dipetakan ke jurnal terindeks Scopus Q1-Q3 dan SINTA. Total 17 referensi unik."
                : "Each game is mapped to Scopus Q1-Q3 and SINTA-indexed journals. 17 unique references."}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ScienceTabContent() {
  const { language } = useGame();
  return (
    <div className="space-y-4" data-testid="science-tab-content">
      <ScienceHeader />
      {[1, 2, 3, 4, 5].map((ageNum) => {
        const layer = LAYERS[ageNum];
        return (
          <Card
            key={ageNum}
            className="rounded-3xl bg-white border-4"
            style={{ borderColor: layer.color }}
            data-testid={`science-layer-${ageNum}`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ background: layer.color }}
                >
                  {ageNum}
                </span>
                {t("pd_layer", language)} {ageNum} — {t(layer.themeKey, language)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {layer.games.map((gKey) => (
                <GameScienceBlock key={gKey} gKey={gKey} layer={layer} />
              ))}
            </CardContent>
          </Card>
        );
      })}
      <div className="text-xs text-slate-500 italic text-center py-4">
        {language === "id"
          ? "ⓘ Dokumen lengkap tersedia di /app/docs/scientific_basis.md. Verifikasi sitasi via portal SINTA atau Scopus sebelum penggunaan akademik formal."
          : "ⓘ Full document at /app/docs/scientific_basis.md. Verify via SINTA or Scopus portal before formal academic use."}
      </div>
    </div>
  );
}
